<script setup lang="ts">
/**
 * A browser task the agent proposed ("Analyze Instagram insights", "Upload Nike reel to Google Drive"): what it
 * will do on which site(s), with what from the workspace, what the creator may need to do themselves, and the
 * one button that starts it.
 * Nothing opens until they click. Once started, the card follows the task (in progress, needs you, done). Once
 * it's over (done or dismissed) it folds to a one-line receipt, so a finished task doesn't keep a full card's room.
 *
 * Phones (bubble chat, or no pane at all, like the voice call screen): full-width 44px buttons, and "Watch the
 * browser" brings up the full-screen live mode. Outside a conversation (the call screen) the card asks the
 * conversation to show its browser (`requestBrowser`) and starts tasks in the phone live view's shape.
 */
import { computed, inject, ref, type Ref } from 'vue'
import { agentApi, AgentApiError } from '../api'
import { browserArtifact } from '../artifacts'
import type { BrowserTaskCard as Card } from '../protocol/types'
import { requestBrowser, useWorkspace } from '../workspace'
import { expectedLiveStage } from './workspace/liveStage'
import useChatChrome from '@/composables/useChatChrome'
import { vPress } from '@/directives/press'

const props = defineProps<{ task: Card; sessionId: string | null; disabled?: boolean }>()
const emit = defineEmits<{ started: [out: { sessionId: string; taskId: string }]; cancelled: [taskId: string]; open: [sessionId: string] }>()

const ws = useWorkspace()
const { isPhone } = useChatChrome()
const bubbles = inject<Ref<boolean> | null>('chatBubbles', null)
/** Phone layout: in the bubble chat, or anywhere on a phone-width screen. */
const phone = computed(() => Boolean(bubbles?.value) || isPhone.value)
const busy = ref(false)
const error = ref<string | null>(null)

const status = computed(() => props.task.status)
/** Done or dismissed: just the title and how it ended. */
const receipt = computed(() => status.value === 'completed' || status.value === 'cancelled')
const statusLabel = computed(() => ({
    proposed: 'Ready when you are', started: 'In progress', waiting_creator: 'Needs you', completed: 'Done',
    cancelled: 'Cancelled', failed: "Didn't finish",
}[status.value] ?? status.value))

function openPane(sessionId: string) {
    if (ws) ws.open(browserArtifact(sessionId, props.task.title))
    else requestBrowser(sessionId)
    emit('open', sessionId)
}

async function start() {
    // With a workspace, the pane opens first and the viewer starts the task once it knows its size (the browser
    // is created in the pane's shape). Without one (no pane to fill), start straight away: on a phone in the
    // shape of the live mode it will be watched in.
    if (ws && props.sessionId) {
        ws.open(browserArtifact(props.sessionId, props.task.title, props.task.task_id))
        emit('started', { sessionId: props.sessionId, taskId: props.task.task_id })
        return
    }
    busy.value = true
    error.value = null
    try {
        const r = await agentApi().startBrowserTask(props.task.task_id, phone.value ? expectedLiveStage() ?? undefined : undefined)
        emit('started', { sessionId: r.session_id, taskId: props.task.task_id })
    } catch (e) {
        error.value = e instanceof AgentApiError ? e.message : "Couldn't start the browser just now. Try again in a moment."
    } finally {
        busy.value = false
    }
}

async function dismiss() {
    busy.value = true
    error.value = null
    try {
        await agentApi().cancelBrowserTask(props.task.task_id)
        emit('cancelled', props.task.task_id)
    } catch (e) {
        error.value = e instanceof AgentApiError ? e.message : "Couldn't dismiss it just now."
    } finally {
        busy.value = false
    }
}
</script>

<template>
    <div class="bt" :class="{ 'is-phone': phone, 'is-receipt': receipt }" data-testid="browser-task-card" :data-status="status">
        <div class="bt-head">
            <span class="bt-icon" aria-hidden="true"><i class="fa-regular fa-window" /></span>
            <div class="bt-text">
                <p class="bt-title">{{ task.title }}</p>
                <p class="bt-sub">
                    <span class="domain">{{ task.domain }}</span>
                    <template v-for="site in task.sites ?? []" :key="site">
                        <span class="dot" aria-hidden="true">·</span>
                        <span class="domain">{{ site }}</span>
                    </template>
                </p>
            </div>
            <span class="bt-status" :data-status="status">{{ statusLabel }}</span>
        </div>
        <p v-if="task.suspicious && !receipt" class="bt-warn" data-testid="browser-task-warning">
            <i class="fa-solid fa-triangle-exclamation" aria-hidden="true" />
            This link doesn't look like it belongs to the sender. Only start it if you trust it.
        </p>
        <ul v-if="task.files?.length && !receipt" class="bt-list" aria-label="Files it will use">
            <li v-for="f in task.files" :key="f"><i class="fa-regular fa-file" aria-hidden="true" />{{ f }}</li>
        </ul>
        <p v-if="task.needs_from_creator?.length && !receipt" class="bt-needs">
            <span class="bt-needs-label">You'll need to:</span> {{ task.needs_from_creator.join(' · ') }}
        </p>
        <div v-if="!receipt" class="bt-actions">
            <template v-if="status === 'proposed'">
                <button v-press type="button" class="agent-btn is-primary" :disabled="busy || disabled" data-testid="browser-task-start" @click="start">
                    <i class="fa-regular fa-window" aria-hidden="true" /> Start browser
                </button>
                <button v-press type="button" class="agent-btn is-quiet" :disabled="busy || disabled" data-testid="browser-task-dismiss" @click="dismiss">Not now</button>
            </template>
            <template v-else-if="status === 'started' || status === 'waiting_creator'">
                <button v-if="sessionId" v-press type="button" class="agent-btn" :class="status === 'waiting_creator' ? 'is-primary' : 'is-soft'" data-testid="browser-task-open" @click="openPane(sessionId)">
                    <i v-if="phone" class="fa-regular" :class="status === 'waiting_creator' ? 'fa-hand' : 'fa-eye'" aria-hidden="true" />
                    {{ status === 'waiting_creator' ? 'Take a look' : 'Watch the browser' }}
                </button>
            </template>
            <template v-else-if="status === 'failed'">
                <button v-press type="button" class="agent-btn is-soft" :disabled="busy || disabled" data-testid="browser-task-start" @click="start">Try again</button>
            </template>
        </div>
        <p v-if="error" class="bt-err">{{ error }}</p>
    </div>
</template>

<style scoped>
.bt {
    margin-top: 10px;
    padding: 14px 16px;
    border-radius: var(--agent-radius-md);
    background: var(--agent-card);
    border: 1px solid var(--agent-card-border);
    box-shadow: var(--agent-card-shadow);
}
.bt-head { display: flex; align-items: flex-start; gap: 12px; }
.bt-icon {
    display: inline-flex; align-items: center; justify-content: center;
    width: 34px; height: 34px; flex-shrink: 0; border-radius: 10px;
    background: var(--agent-accent-soft); color: var(--agent-accent); font-size: 15px;
}
.bt-text { flex: 1; min-width: 0; }
.bt-title { font-size: 14px; font-weight: 600; line-height: 20px; color: var(--agent-text-1); }
.bt-sub { display: flex; gap: 6px; align-items: baseline; font-size: 12px; color: var(--agent-text-3); }
.domain { font-variant-numeric: tabular-nums; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bt-status {
    flex-shrink: 0; font-size: 11.5px; font-weight: 600; padding: 3px 8px; border-radius: 999px;
    background: var(--agent-hover); color: var(--agent-text-3);
}
.bt-status[data-status='waiting_creator'] { background: var(--agent-accent-soft); color: var(--agent-accent); }
.bt-status[data-status='completed'] { background: rgb(16 185 129 / 0.12); color: #047857; }
.bt-status[data-status='started'] { color: var(--agent-text-2); }
.bt-warn {
    display: flex; gap: 8px; align-items: flex-start; margin-top: 10px; padding: 8px 10px; border-radius: 10px;
    font-size: 12.5px; line-height: 18px; background: rgb(245 158 11 / 0.12); color: #92400e;
}
.bt-list { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
.bt-list li {
    display: inline-flex; align-items: center; gap: 6px; padding: 4px 9px; border-radius: 999px;
    font-size: 12px; color: var(--agent-text-2); background: var(--agent-hover);
}
.bt-needs { margin-top: 10px; font-size: 12.5px; line-height: 18px; color: var(--agent-text-2); }
.bt-needs-label { font-weight: 600; color: var(--agent-text-3); }
.bt-actions { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 12px; }
.bt-actions:empty { display: none; }
/* Over (done or dismissed): one quiet line. */
.bt.is-receipt { padding: 9px 12px; box-shadow: none; }
.bt.is-receipt .bt-head { align-items: center; gap: 10px; }
.bt.is-receipt .bt-icon { width: 28px; height: 28px; border-radius: 8px; font-size: 12.5px; background: var(--agent-hover); color: var(--agent-text-3); }
.bt.is-receipt .bt-title { font-size: 13px; font-weight: 500; color: var(--agent-text-2); }
.bt-err { margin-top: 8px; font-size: 12px; color: var(--agent-text-3); }
.bt-actions .agent-btn i { margin-right: 2px; }

/* Phones: the card family's shape (22px, a hairline ring instead of a border), the status pill under the title so
   the title keeps the row, and full-width 44px buttons, the main one first. */
.bt.is-phone {
    margin-top: 0;
    padding: 14px;
    border: 0;
    border-radius: 22px;
    box-shadow: 0 0 0 1px var(--agent-card-border), 0 8px 22px -12px rgb(15 15 15 / 0.22);
}
html.dark .bt.is-phone { box-shadow: 0 0 0 1px var(--agent-card-border), 0 10px 26px -12px rgb(0 0 0 / 0.7); }
.bt.is-phone .bt-head { display: grid; grid-template-columns: 34px minmax(0, 1fr); column-gap: 12px; row-gap: 8px; }
.bt.is-phone .bt-icon { grid-row: 1 / span 2; }
.bt.is-phone .bt-status { grid-column: 2; justify-self: start; }
.bt.is-phone .bt-title { font-size: 15px; }
.bt.is-phone .bt-sub { min-width: 0; overflow: hidden; font-size: 12.5px; }
.bt.is-phone .bt-actions { flex-direction: column; gap: 8px; margin-top: 14px; }
.bt.is-phone .bt-actions .agent-btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    width: 100%; height: 44px; border-radius: 999px; font-size: 15px;
}
.bt.is-phone.is-receipt { padding: 10px 14px; }
.bt.is-phone.is-receipt .bt-head { display: flex; }
</style>
